import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import { ease, fadeUp, scaleIn, stagger, viewport, press } from "../lib/motion";
import "../styles/VideoSection.css";

const QUERY = `*[_type == "videoSection"][0]{
  title, subtitle, languages, videoLinks, thumbnail, playIcon
}`;


const VideoSection = () => {
  const [data, setData] = useState(null);
  const [language, setLanguage] = useState("English");
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(setData);
  }, []);

  const videoLinkMap = {};
  if (data?.videoLinks) {
    data.videoLinks.forEach((link) => {
      videoLinkMap[link.language] = link.url;
    });
  }
  const handlePlayClick = () => {
    setIsPlaying(true);
  };

  // Load YouTube Iframe API
  React.useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  }, []);

  // Initialize Player when isPlaying becomes true
  React.useEffect(() => {
    if (isPlaying && window.YT && window.YT.Player) {
      // Give the iframe a small delay to ensure it's in the DOM
      const timer = setTimeout(() => {
        new window.YT.Player("repository-video", {
          events: {
            onStateChange: (event) => {
              // YT.PlayerState.ENDED is 0
              if (event.data === 0) {
                setIsPlaying(false);
              }
            },
          },
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, language]);

  if (!data) return null;

  const langs = (data.languages || []).map((l) => l.label);

  return (
    <section className="video-section" id="video">
      <div className="video-container">
        {/* Radial Glow Background */}
        <motion.div
          className="radial-background"
          animate={{ scale: [1, 1.06, 1], opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <motion.div
          className="video-head"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.12)}
        >
          <motion.h2 className="video-title" variants={fadeUp}>
            {data.title}
          </motion.h2>
          <motion.p className="video-subtitle" variants={fadeUp}>
            {data.subtitle}
          </motion.p>

          {/* Language Toggle Buttons */}
          <motion.div className="language-toggle" variants={fadeUp}>
            {langs.map((lang) => (
              <motion.button
                key={lang}
                className={language === lang ? "active" : ""}
                onClick={() => {
                  setLanguage(lang);
                  setIsPlaying(false);
                }}
                whileTap={{ scale: 0.95 }}
              >
                {language === lang && (
                  <motion.span
                    className="language-pill"
                    layoutId="language-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className="language-label">
                  {lang === "Transformative Impact"
                    ? "Transformative Impact of BharatGPT"
                    : lang}
                </span>
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        {/* Video Block */}
        <motion.div
          className="video-wrapper-container"
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={scaleIn}
        >
          <div className="video-bg-rectangle left"></div>
          <div className="video-bg-rectangle right"></div>

          <AnimatePresence mode="wait">
            <motion.div
              className="video-wrapper"
              key={isPlaying ? "iframe" : "thumbnail"}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.45, ease }}
            >
              {!isPlaying ? (
                <>
                  <img
                    src={urlFor(data.thumbnail).url()}
                    alt="Demo Video"
                    className="video-thumbnail"
                  />
                  <motion.button
                    className="play-button"
                    onClick={handlePlayClick}
                    aria-label="Play video"
                    {...press}
                  >
                    <img src={urlFor(data.playIcon).url()} alt="Play" />
                  </motion.button>
                </>
              ) : (
                <motion.iframe
                  id="repository-video"
                  className="video-iframe"
                  src={videoLinkMap[language]}
                  title={`${language} Demo Video`}
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default VideoSection;
